import {
  memo, ReactElement, useCallback,
} from 'react';
import cn from 'classnames';
import { createUseStyles } from 'react-jss';
import Button from 'components/Button';

type NumberInputProps = {
  label?: string,
  value?: number,
  min?: number,
  max?: number,
  onChange?: (value: number | undefined) => void,
  className?: string,
  isDisabled?: boolean,
};

const NumberInput = (props: NumberInputProps): ReactElement => {
  const {
    label, value, onChange, className, max, min, isDisabled,
  } = props;

  const classes = useClasses();

  const handleIncrement = useCallback(() => {
    if (typeof value !== 'number') {
      return;
    }
    if (typeof max === 'undefined') {
      onChange?.(value + 1);
      return;
    }
    if (value < max) {
      onChange?.(value + 1);
    }
  }, [onChange, value, max]);

  const handleDecrement = useCallback(() => {
    if (typeof value !== 'number') {
      return;
    }
    if (typeof min === 'undefined') {
      onChange?.(value - 1);
      return;
    }
    if (value > min) {
      onChange?.(value - 1);
    }
  }, [onChange, value, min]);

  return (
    <div className={cn(className, classes.NumberInput)}>
      {label && (
        <div className={classes.label}>{label}</div>
      )}
      <div className={classes.input}>
        <Button
          type="button-icon"
          width={44}
          height={44}
          htmlColor="#20B2A4"
          isDisabled={isDisabled}
          onPress={handleDecrement}
        >
          -
        </Button>
        <div className={`${classes.input}--value`}>{value}</div>
        <Button
          type="button-icon"
          width={44}
          height={44}
          htmlColor="#20B2A4"
          isDisabled={isDisabled}
          onPress={handleIncrement}
        >
          +
        </Button>
      </div>
    </div>
  );
};

const useClasses = createUseStyles({
  NumberInput: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    color: '#454545',
  },
  label: {
    fontWeight: 500,
    fontSize: '1.5rem',
    color: '#3F3F3F',
    marginBottom: '0.8rem',
  },
  input: {
    '&': {
      fontFamily: 'Avenir Next, sans-serif',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
    },
    '&--value': {
      fontWeight: 600,
      fontSize: '2.4rem',
      color: '#3F3F3F',
      width: '5rem',
      textAlign: 'center',
    },
  },
});

export default memo(NumberInput);
